import { DateTime } from "luxon";
import { isNullOrWhitespace } from "@utils/utility";
import { SGUID_EMPTY_CONST, isGamesTheme } from "@utils";
import LookupApiService from "@api-services/LookupApiService";
import { UserPaymentTransactionsApiService } from "@api-services/user/UserPaymentTransactionsApiService";

import { AccountStatementApiService } from "@api-services/account-settings/AccountStatementApiService";
import {
	AccountActivityReviewReportDto,
	BettingActivityReviewReportDto,
} from "@api-types/reports/ReportResultDto";
import { AccountStatementFilter } from "@api-types/account-settings/AccountStatementFilter";
import { BaseLookupDto } from "@api-types/lookups/BaseLookupDto";

import { ReportOptionsDto } from "@api-types/reports/ReportOptionsDto";
import { UserPaymentTransactionLimited } from "@api-types/account-settings/UserPaymentTransactionLimited";
import { UserAccountType } from "@common/constants";
import { OfferService } from "@api-services/offer/OfferApiService";
import { getCurrentCulture } from "@utils";
import { LazyImportWithLoadFailHandle as lazy } from "@lib/lazy-import-with-guard/LazyImportWithGuard";
import { BetSlipEvent } from "@api-types/agency/bet-slip/BetSlipEvent";
import { BetSlipEventInOffer } from "../../types/account-statement/AccountStatementBetSlip";
import { Periods } from "@data-types/account-statement/Periods";
import {
	FilterEventId,
	filterParams,
} from "@data-types/account-statement/FilterEventId";
import { UserPaymentTransactionLimitedHasDetails } from "@data-types/account-statement/UserPaymentTransactionLimitedHasDetails";
import { SubmitFilter } from "@data-types/account-statement/SubmitFilter";
import { UserActivityTypes } from "@data-types/account-statement/UserActivityTypes";
import { TransactionTableResponse } from "@data-types/account-statement/TransactionTableResponse";
import { UserAccount } from "@data-types";
import { RequestContextType } from "@utils/api-config/RequestContext";
import { createApiRequestOptions } from "@utils/api-config/createApiRequestOptions";
import ReportInsightsApiService from "@api-services/reports/ReportInsightsApiService";

const loadFailPath = `/${getCurrentCulture()}/app-update`;

// prettier-ignore
const BetSlipApiService = lazy(loadFailPath, ()=>import("@api-services/agency/BetSlipApiService"));

export const PERIODS = [
	{ name: "ACC_STATEMENT.FILTER.TODAY", id: "today" },
	{ name: "ACC_STATEMENT.FILTER.LAST_3_DAYS", id: "last3days" },
	{ name: "ACC_STATEMENT.FILTER.LAST_7_DAYS", id: "last7days" },
	{ name: "ACC_STATEMENT.FILTER.LAST_14_DAYS", id: "last14days" },
	{ name: "ACC_STATEMENT.FILTER.LAST_30_DAYS", id: "last30days" },
	{ name: "ACC_STATEMENT.FILTER.LAST_60_DAYS", id: "last60days" },
	{ name: "ACC_STATEMENT.FILTER.CUSTOM", id: "custom" },
];

export type TransactionLookup = {
	accountTypeId: string;
	transactionTypes: void | BaseLookupDto[];
	abrv: UserAccountType;
};

export type rawFilter = {
	pageNumber?: number;
	asPeriod: TPeriod;
	endDate?: string;
	startDate?: string;
	asType: string;
	bettingAccountTypeAbrv: string;
	shopId: string;
};

export const CUSTOM_PERIOD = "custom";
const DEFAULT_RPP = 10;
const DEFAULT_SORT = "dateCreated|desc";
const DEFAULT_EMBED =
	"transactionType,currency,BetSlipStatus,TransactionStatus,BetStatus";

const ERROR_OCCURED = "ERROR_OCCURED";

export type TPeriod = Periods | typeof CUSTOM_PERIOD | string | null;
export class AccountStatementService {
	static async getTransactionLookup(
		wallet: UserAccount
	): Promise<TransactionLookup | null> {
		try {
			return {
				abrv: wallet.abrv,
				accountTypeId: wallet.accountTypeId,
				transactionTypes:
					await LookupApiService.getAccountStatementTypes({
						context: RequestContextType.Wallet,
						walletId: wallet.id,
					}),
			};
		} catch (e) {
			console.error(e);
			return null;
		}
	}
	//Get USER TRANSACTIONS BETS SLIP,ETC...
	static async fetchTransactionsTableData(
		model: SubmitFilter,
		page: number = 1
	): Promise<TransactionTableResponse> {
		const isTransactionTypeValid = (transactionTypeId: string) => {
			return (
				!isNullOrWhitespace(transactionTypeId) &&
				transactionTypeId !== "all"
			);
		};

		try {
			const filterParametars: Parameters<
				(typeof AccountStatementService)["setupUserTransactionFilter"]
			>[0] = {
				page: page,
				dates:
					model.asPeriod == CUSTOM_PERIOD
						? null
						: this.fromToDates(model.asPeriod),
				rpp: DEFAULT_RPP,
				sort: DEFAULT_SORT,
				embed: DEFAULT_EMBED,
				to: model.endDate!,
				from: model.startDate!,
				transactionTypesIds: isTransactionTypeValid(model.asType)
					? model.asType
					: "",
				bettingAccountTypeIds: model.bettingTypeIds,
			};

			const filter = this.setupUserTransactionFilter(filterParametars);

			const transactionQuery = this.createQuery(filter);

			const result =
				await AccountStatementApiService.findAccountStatements(
					transactionQuery,
					{
						context: RequestContextType.Wallet,
						walletId: model.activeWallet?.id,
					}
				);

			return {
				transactions: this.filterAccountStatementDetails(result.item),
				totalRecords: result.totalRecords,
				page: result.page,
				recordsPerPage: result.recordsPerPage,
			};
		} catch (error) {
			console.error(error);
			throw new Error("ERROR_OCCURED");
		}
	}

	static async getBettingAndPaymentTableData(
		model: rawFilter,
		wallet: UserAccount
	): Promise<UserActivityTypes | null> {
		try {
			const reportFilter: ReportOptionsDto = {
				Filter: {
					AccessChannels: {
						Agencies: null,
						Shops: [
							{
								AgencyId: wallet.agencyId,
								Id: wallet.shopId,
							},
						],
					},
					Parameters: [
						{
							Column: "StartDate",
							Value: "0",
						},
					],
				},
				Paging: null,
			};
			if (model.asPeriod == "custom") {
				const now = DateTime.now();
				const startDate = DateTime.fromISO(model.startDate!);
				const endDate = DateTime.fromISO(model.endDate!);
				let startDateCustom = now
					.diff(startDate, ["days"])
					.toObject().days!;
				let endDateCustom = now
					.diff(endDate, ["days"])
					.toObject().days!;
				reportFilter.Filter.Parameters = [
					{
						Column: "StartDate",
						Value: Math.floor(startDateCustom) + "",
					},
				];

				if (startDateCustom !== endDateCustom) {
					reportFilter.Filter.Parameters.push({
						Column: "EndDate",
						Value: Math.floor(endDateCustom) + "",
					});
				}
			} else {
				const period = AccountStatementService.fromToDates(
					model.asPeriod
				);
				const date1 = DateTime.fromISO(period.from);
				const date2 = DateTime.fromISO(period.to);
				const diffInDays = Math.floor(
					date2.diff(date1, ["days"]).toObject().days!
				);

				reportFilter.Filter.Parameters = [
					{
						Column: "StartDate",
						Value: diffInDays + "",
					},
				];
			}

			if (model.bettingAccountTypeAbrv.length) {
				reportFilter.Filter.Parameters.push({
					Column: "BettingAccountTypeAbrv",
					Value: model.bettingAccountTypeAbrv,
				});
			}

			const userActivityResult =
				await ReportInsightsApiService.fetchUserAccountActivity(
					reportFilter,
					{
						walletId: wallet.id,
						context: RequestContextType.Wallet,
					}
				);

			if (isGamesTheme()) {
				if (
					userActivityResult?.isSuccess &&
					userActivityResult.result
				) {
					userActivityResult.result = (
						userActivityResult.result as AccountActivityReviewReportDto[]
					).filter((item) => item.BettingAccountTypeAbrv === "games");
				}
			}

			let bettingActivityResult = null;

			if (!isGamesTheme()) {
				bettingActivityResult =
					await ReportInsightsApiService.fetchUserBettingActivity(
						reportFilter,
						{
							walletId: wallet.id,
							context: RequestContextType.Wallet,
						}
					);
			}

			let userActivities: AccountActivityReviewReportDto[] = [];
			let bettingActivities: BettingActivityReviewReportDto[] = [];

			if (
				userActivityResult.result != null &&
				userActivityResult.result.length > 0
			) {
				userActivityResult.result.forEach((item) => {
					const activity = item;
					if (
						"NumberOfTransactions" in activity &&
						activity.NumberOfTransactions > 0
					) {
						userActivities.push(activity);
					}
				});
			}

			if (
				bettingActivityResult &&
				bettingActivityResult.result != null &&
				bettingActivityResult.result.length > 0
			) {
				bettingActivityResult.result.forEach((item) => {
					const activity = item;
					if (
						"NumberOfBets" in activity &&
						activity.NumberOfBets > 0
					) {
						bettingActivities.push(activity);
					}
				});
			}
			return {
				userActivity: userActivities,
				bettingActivity: bettingActivities,
			};
		} catch (error) {
			console.error(error);
			throw ERROR_OCCURED;
		}
	}

	static async getSlipDetails(
		betSlipId: string,
		wallet: UserAccount | null
	): Promise<BetSlipEventInOffer | null> {
		if (!wallet) {
			throw ERROR_OCCURED;
		}
		try {
			const slip = await (
				await BetSlipApiService
			).default.getBet(
				betSlipId,
				"betSlipOffers,player.coreUser,betSlipStatus,betStatus,betSlipType,betSlipCombinations,bettingAccountType",
				createApiRequestOptions(RequestContextType.Wallet, wallet.id)
			);

			const initialEventIds = this.filterEventIds(slip.betSlipOffers);

			const eventIds = await this.filterEventIdsWithOffer(
				initialEventIds,
				wallet
			);

			const betSlipOfferMapped = slip.betSlipOffers.map((offer) => ({
				...offer,
				isEventInOffer: offer.eventId
					? eventIds.includes(offer.eventId)
					: false,
			}));

			return {
				...slip,
				betSlipOffers: [...betSlipOfferMapped],
			};
		} catch (error) {
			console.error(error);
			throw new Error();
		}
	}

	static async getTransactionDetails(
		transactionId: string,
		wallet: UserAccount | null
	): Promise<UserPaymentTransactionLimited> {
		try {
			const transaction =
				await UserPaymentTransactionsApiService.getTransactionDetails(
					transactionId,
					createApiRequestOptions(
						RequestContextType.Wallet,
						wallet?.id
					)
				);
			return transaction;
		} catch (error) {
			console.error(error);
			throw new Error();
		}
	}

	/**
	 * Filters event ids and returns only those that have offer
	 * @param {array} eventIds list of event ids
	 * @returns {Promise} array of event Ids that are in offer
	 */
	static async filterEventIdsWithOffer(
		eventIds: FilterEventId,
		wallet: UserAccount
	) {
		try {
			const response = await OfferService.findOffer(
				eventIds,
				createApiRequestOptions(RequestContextType.Wallet, wallet.id)
			);

			const eventIdsInOffer =
				response.offerChanges?.updates?.map(
					(eventObj: { eventId: string }) => eventObj.eventId
				) || [];

			return eventIdsInOffer;
		} catch (err) {
			console.error(err);
		}
		// default in case of exception
		return [];
	}

	static filterEventIds(offers: BetSlipEvent[]): FilterEventId {
		const eventIds = offers.map((eventObj) => eventObj.eventId) || [];

		return {
			channels: [
				{
					name: "event",
					filter: [
						{
							startTime: {
								gt: DateTime.local()
									.plus({ days: -1 })
									.toUTC()
									.toString(),
							},
							id: {
								eq: eventIds,
							},
						},
					],
				},
			],
		};
	}

	static filterAccountStatementDetails(
		items: UserPaymentTransactionLimited[]
	): UserPaymentTransactionLimitedHasDetails[] {
		let mappedItems = items;
		return mappedItems.map(
			(item: UserPaymentTransactionLimitedHasDetails) => {
				item.hasDetails =
					item.userPaymentTransactionId == SGUID_EMPTY_CONST &&
					item.betSlipId == SGUID_EMPTY_CONST
						? false
						: true;
				return item;
			}
		);
	}

	static setupUserTransactionFilter(filterParams: filterParams) {
		const filter: AccountStatementFilter = {
			embed: filterParams.embed,
			page: filterParams.page,
			rpp: filterParams.rpp,
			bettingAccountTypeIds: filterParams.bettingAccountTypeIds,
		};

		if (filterParams.dates != null) {
			filter.from = filterParams.dates.from;
			filter.to = filterParams.dates.to;
		} else {
			if (filterParams.to != null) {
				filter.to = filterParams.to;
			}
			if (filterParams.from != null) {
				filter.from = filterParams.from;
			}
		}

		if (!isNullOrWhitespace(filterParams.transactionTypesIds)) {
			filter.transactionTypeIds = filterParams.transactionTypesIds;
		}

		filter.bettingAccountTypeIds = filterParams.bettingAccountTypeIds;

		return filter;
	}

	static fromToDates(period: Periods | string | null): {
		from: string;
		to: string;
	} {
		const now = DateTime.now();
		const to = now;
		let from;

		switch (period) {
			case "today":
				from = now.startOf("day");
				break;
			case "last3days":
				from = now.startOf("day").minus({ days: 3 });
				break;
			case "last7days":
				from = now.startOf("day").minus({ days: 7 });
				break;
			case "last14days":
				from = now.startOf("day").minus({ days: 14 });
				break;
			case "last30days":
				from = now.startOf("day").minus({ days: 30 });
				break;
			case "last60days":
				from = now.startOf("day").minus({ days: 60 });
				break;
			default:
				from = now.startOf("day");
		}

		return {
			from: from.toJSDate().toISOString(),
			to: to.toJSDate().toISOString(),
		};
	}

	static createQuery(filter: AccountStatementFilter): AccountStatementFilter {
		const queryParams: AccountStatementFilter = {
			embed: filter.embed,
			page: filter.page,
			rpp: filter.rpp,
			bettingAccountTypeIds: filter.bettingAccountTypeIds,
		};

		if (filter.from != null) {
			queryParams.from = filter.from;
		}

		if (filter.to != null) {
			queryParams.to = filter.to;
		}

		if (
			filter.transactionTypeIds != null &&
			!isNullOrWhitespace(filter.transactionTypeIds)
		) {
			queryParams.transactionTypeIds = filter.transactionTypeIds;
		}

		if (filter.bettingAccountTypeIds) {
			queryParams.bettingAccountTypeIds = filter.bettingAccountTypeIds;
		}
		return queryParams;
	}
}
